<template><render /></template>

<script setup lang="ts">
import {
  type AsyncComponentLoader,
  type Component,
  defineAsyncComponent,
  h,
  inject,
  provide,
} from 'vue';

import { useFischer } from '@/composables/fischerPlugin';
import type { Placeholder } from '@/sitecoreState';

const props = defineProps<{
  componentName: string;
  fields: object;
  placeholders: object;
  placeholderIdx: number;
  placeholderProps?: object;
  placeholder: Placeholder;
  wrapperComponent?: Component;
  wrapperProps?: object;
}>();

const componentFactory =
  inject<
    (componentName: string | undefined) => Component | AsyncComponentLoader<Component> | undefined
  >('componentFactory');
if (!componentFactory) {
  throw new Error('missing inject componentFactory');
}

const fischer = useFischer();

const fields = props.fields || {};
provide('fields', fields);

const getComponent = () => {
  const fromFactory = componentFactory(props.componentName);
  if (!fromFactory) {
    const msg = `component not found for field injection: ${props.componentName}`;
    console.error(msg);
    fischer.markSsrError(msg);
    return h('pre', null, msg);
  }
  if (fromFactory instanceof Promise) {
    return defineAsyncComponent(() => fromFactory);
  }
  return fromFactory;
};

const asyncComponent = getComponent();

const wrappedComponent = props.wrapperComponent ? props.wrapperComponent : asyncComponent;
const placeholderProps = {
  fields,
  placeholders: props.placeholders,
  placeholderProps: props.placeholderProps,
  placeholderIdx: props.placeholderIdx,
  placeholder: props.placeholder,
};
const wrapperProps: object =
  typeof props.wrapperProps === 'object'
    ? { ...placeholderProps, ...placeholderProps }
    : placeholderProps;
const render = h(wrappedComponent, placeholderProps, {
  default: () => h(asyncComponent, wrapperProps),
});
</script>
